import ReactTooltip from 'react-tooltip'
import * as Yup from 'yup'
import { format, toZonedTime } from 'date-fns-tz'
import { isAfter } from 'date-fns'
import { startCase } from 'lodash'
import { Link } from 'react-router-dom'
import { PencilSimple } from '@phosphor-icons/react'

import { EMAIL_REGEX } from '../../helpers/constants'
import {
  getFullDateFromDayMinutes,
  dayMinutesToHoursMinutes,
  cutTooLong,
  canCancelReservation,
} from '../../helpers/helpers'
import { getAssetLink } from '../../helpers/get-asset-link'
import StatusPill from '../../components/StatusPill/StatusPill'
import DropdownDots from '../DropdownDots/DropdownDots'
import Icon from '../Icons'
import Filter from '../Filter/Filter'
import Badge from '../Badge/Badge'

import TextCell from './components/TextCell/TextCell'

export const makeReservationForClientValidation = (t) => ({
  guestFirstName: Yup.string().required(t('reservations:validation.required')),
  guestLastName: Yup.string().required(t('reservations:validation.required')),
  guestEmail: Yup.string()
    .matches(EMAIL_REGEX, t('reservations:validation.guestEmailInvalid'))
    .required(t('reservations:validation.required')),
  guestPhone: Yup.string(),
  cardholderName: Yup.string().required(t('reservations:validation.required')),
})

export const getReservationsStatusFilterOptions = (t) => [
  {
    id: 0,
    text: t('reservations:statusFilterOptions.all'),
    value: '',
  },
  {
    id: 1,
    text: t('reservations:statusFilterOptions.pending'),
    value: 'pending',
  },
  {
    id: 2,
    text: t('reservations:statusFilterOptions.approved'),
    value: 'approved',
  },
  {
    id: 3,
    text: t('reservations:statusFilterOptions.canceled'),
    value: 'cancelled',
  },
  {
    id: 4,
    text: t('reservations:statusFilterOptions.completed'),
    value: 'completed',
  },
  {
    id: 5,
    text: t('reservations:statusFilterOptions.blocked'),
    value: 'blocked',
  },
  {
    id: 6,
    text: t('reservations:statusFilterOptions.ongoing'),
    value: 'ongoing',
  },
]

export const columns = (
  {
    timezone,
    role,
    onShowDetails = () => {},
    onCancelReservation = () => {},
    onDecline = () => {},
    onApprove = () => {},
    onShowNotes = () => {},
    onDeleteTimeBlock = () => {},
  },
  t,
) => {
  return [
    {
      Header: t('reservations:table.residentNameUnit'),
      id: 'residentNameUnit',

      Cell: ({ row: { original } }) => {
        const { residentName, residentUnit, status, guestName, source } = original
        if (status === 'blocked') {
          return t('reservations:table.timeBlock')
        }

        const isFromMarketplace = source === 'Marketplace'

        return (
          <TextCell
            title={cutTooLong(guestName ? guestName : residentName, 25)}
            text={guestName ? null : cutTooLong(residentUnit, 25)}
            badge={
              isFromMarketplace ? (
                <Badge className="text-cell-badge-marketplace" color="green-filled">
                  {t('reservations:table.amenityMarketplaceBadge')}
                </Badge>
              ) : null
            }
          />
        )
      },
    },
    {
      Header: t('reservations:table.amenity'),
      id: 'amenity',
      Cell: ({ row: { original } }) => {
        const { locationId, locationName, amenityName } = original
        return locationName && locationId ? (
          <TextCell title={cutTooLong(amenityName, 25)} text={`/${cutTooLong(locationName, 25)}`} />
        ) : (
          cutTooLong(amenityName, 25)
        )
      },
    },
    {
      Header: t('reservations:table.startDate'),
      id: 'startDate',

      Cell: ({ row: { original } }) => {
        const { date, endDate, startTime } = original
        const isMultiDate = !!endDate

        return date ? (
          <TextCell
            title={format(toZonedTime(date, timezone), 'MM/dd/yyyy', { timeZone: timezone })}
            text={
              !isMultiDate ? format(getFullDateFromDayMinutes(startTime).valueOf(), 'hh:mm a') : ''
            }
          />
        ) : (
          '-'
        )
      },
    },
    {
      Header: t('reservations:table.endDate'),
      id: 'endDate',

      Cell: ({ row: { original } }) => {
        const { date, endDate, endTime } = original
        const isMultiDate = !!endDate

        return endDate || date ? (
          <TextCell
            title={format(toZonedTime(isMultiDate ? endDate : date, timezone), 'MM/dd/yyyy', {
              timeZone: timezone,
            })}
            text={
              !isMultiDate ? format(getFullDateFromDayMinutes(endTime).valueOf(), 'hh:mm a') : ''
            }
          />
        ) : (
          '-'
        )
      },
    },
    {
      Header: t('reservations:table.total'),
      accessor: 'total',

      Cell: ({ value }) => (value ? `$${value}` : '-'),
    },
    {
      Header: t('reservations:table.createdBy'),
      accessor: 'createdByUser',

      Cell: ({ row: { original } }) => {
        const { createdByUser, createdAt } = original
        return createdByUser ? (
          <TextCell
            title={`${createdByUser.firstName} ${createdByUser.lastName}`}
            text={`${format(toZonedTime(createdAt, timezone), 'hh:mm a', {
              timeZone: timezone,
            })} on ${format(toZonedTime(createdAt, timezone), 'MM/dd/yyyy', {
              timeZone: timezone,
            })}`}
          />
        ) : (
          '-'
        )
      },
    },
    {
      Header: t('reservations:table.approvedBy'),
      id: 'approvedBy',

      Cell: ({ row: { original } }) => {
        const { approved, approvedByUser, approvedAt } = original
        if (approved) {
          return !approvedByUser ? (
            t('reservations:table.autoApproved')
          ) : (
            <TextCell
              title={`${approvedByUser.firstName} ${approvedByUser.lastName}`}
              text={`${format(toZonedTime(approvedAt, timezone), 'hh:mm a', {
                timeZone: timezone,
              })} on ${format(toZonedTime(approvedAt, timezone), 'MM/dd/yyyy', {
                timeZone: timezone,
              })}`}
            />
          )
        }

        return '-'
      },
    },
    {
      Header: t('reservations:table.status'),
      accessor: 'status',

      Cell: ({ value }) => <StatusPill title={startCase(value)} />,
    },
    {
      id: 'details_info',

      Cell: ({ row: { original } }) => {
        const { status } = original

        if (status === 'pending') {
          return (
            <div className="cell-actions">
              <div
                data-tip
                data-for="decline-tip"
                className="cell-action-item decline"
                onClick={(e) => {
                  e.stopPropagation()
                  onDecline(original)
                }}
              >
                <Icon icon="cross" />
                <ReactTooltip
                  className="tooltip"
                  id="decline-tip"
                  multiline={true}
                  place="top"
                  effect="solid"
                >
                  {t('reservations:table.decline')}
                </ReactTooltip>
              </div>
              <div
                data-tip
                data-for="approve-tip"
                className="cell-action-item approve"
                onClick={(e) => {
                  e.stopPropagation()
                  onApprove(original)
                }}
              >
                <Icon icon="checkmark" viewBox="0 6 30 20" />
                <ReactTooltip
                  className="tooltip"
                  id="approve-tip"
                  multiline={true}
                  place="top"
                  effect="solid"
                >
                  {t('reservations:table.approve')}
                </ReactTooltip>
              </div>
            </div>
          )
        }

        const options = [
          {
            id: 1,
            text: t('reservations:table.notes'),
            action: () => onShowNotes(original),
          },
        ]

        if (status === 'blocked') {
          options.push({
            id: 3,
            text: t('common:actions.delete'),
            action: () => onDeleteTimeBlock(original),
          })
          return <DropdownDots options={options} />
        }

        options.push({
          id: 0,
          text: t('reservations:table.details'),
          action: () => onShowDetails(original),
        })

        if (canCancelReservation(role, status)) {
          options.push({
            id: 2,
            text: t('reservations:table.cancelReservation'),
            action: () => onCancelReservation(original),
          })
        }

        return <DropdownDots options={options} />
      },
    },
  ]
}

export const mapBookingInfo = (
  {
    id,
    clientId,
    date,
    endDate,
    startTime,
    endTime,
    amenityId,
    status,
    questions,
    signedAgreementFile,
    locationId,
  },
  timezone,
  locations = [],
  onUpdateSpace = () => {},
  t,
) => {
  const isMultiDate = !!endDate

  const spaceOptions = [
    {
      id: locationId?.id,
      text: locationId?.name,
    },
    ...locations.map((location) => ({
      id: location.id,
      text: location.name,
    })),
  ]

  const canEditSpaceStatuses = ['pending', 'approved']
  const now = toZonedTime(new Date(), timezone)
  const startDate = toZonedTime(date, timezone)

  const canEditSpace =
    locationId?.id &&
    spaceOptions.length > 0 &&
    canEditSpaceStatuses.includes(status) &&
    isAfter(startDate, now)

  const result = [
    {
      title: t('reservations:details.reservationId'),
      value: id,
    },
    {
      title: t('reservations:details.unit'),
      value: clientId && clientId.apartment,
      hidden: !clientId,
    },
    {
      title: t('reservations:details.startDateTime'),
      value: `${format(toZonedTime(date, timezone), 'MM/dd/yyyy', { timeZone: timezone })} ${
        !isMultiDate ? format(getFullDateFromDayMinutes(startTime).valueOf(), 'hh:mm a') : ''
      }`,
    },
    {
      title: t('reservations:details.endDateTime'),
      value: `${format(toZonedTime(isMultiDate ? endDate : date, timezone), 'MM/dd/yyyy', {
        timeZone: timezone,
      })} ${!isMultiDate ? format(getFullDateFromDayMinutes(endTime).valueOf(), 'hh:mm a') : ''}`,
    },
    {
      title: t('reservations:details.amenityName'),
      value: amenityId.name,
    },
    {
      title: t('reservations:details.space'),
      label: (
        <Filter
          name="space"
          config={{ multiple: false, hasSearch: false }}
          value={locationId ? [spaceOptions.find((option) => option.id === locationId?.id)] : []}
          placeholder={
            locationId
              ? spaceOptions.find((option) => option.id === locationId?.id)?.text
              : t('reservations:details.selectSpacePlaceholder')
          }
          options={spaceOptions}
          onChange={(event) => {
            if (locationId?.id === event?.target?.value?.id) return
            onUpdateSpace(event?.target?.value?.id)
          }}
          disabled={!canEditSpace}
        />
      ),
      hidden: !locationId?.id || !spaceOptions.length,
    },
    {
      title: t('reservations:table.status'),
      value: startCase(status),
    },
    {
      title: t('reservations:details.agreement'),
      value: (
        <a
          href={getAssetLink({ path: 'reservation_agreements', file: signedAgreementFile })}
          target="_blank"
          rel="noreferrer"
        >
          {signedAgreementFile}
        </a>
      ),
      hidden: !signedAgreementFile,
    },
  ]

  if (questions && Array.isArray(questions) && questions.length) {
    questions.forEach((item) => {
      result.push({
        title: item.question,
        label: item.answer,
      })
    })
  }

  return result
}

export const mapCustomerInformation = ({ clientId, guestId }, t) => {
  const { phone, user } = clientId || guestId
  return [
    {
      title: t('reservations:details.firstName'),
      value: user ? user.firstName : guestId.firstName,
    },
    {
      title: t('reservations:details.lastName'),
      value: user ? user.lastName : guestId.lastName,
    },
    {
      title: t('reservations:details.email'),
      value: user ? user.email : guestId.email,
    },
    {
      title: t('reservations:details.phone'),
      value: phone,
      hidden: !phone,
    },
  ]
}

export const mapAdministrativeInfo = (
  {
    createdAt,
    createdByUser,
    approvedByUser,
    approvedAt,
    refunded,
    refundedAt,
    refundAmount,
    refundedByUser,
    cancelled,
    cancelledAt,
    cancelledBy,
    cancelledByUser,
  },
  timezone,
  t,
) => [
  {
    title: t('reservations:details.createDate'),
    value: format(toZonedTime(createdAt, timezone), 'MM/dd/yyyy hh:mm a', {
      timeZone: timezone,
    }),
  },
  {
    title: t('reservations:details.createdBy'),
    value: createdByUser && `${createdByUser.firstName} ${createdByUser.lastName}`,
    hidden: !createdByUser,
  },
  {
    title: t('reservations:details.approveDate'),
    value:
      approvedAt &&
      format(toZonedTime(approvedAt, timezone), 'MM/dd/yyyy hh:mm a', { timeZone: timezone }),
    hidden: !approvedAt,
  },
  {
    title: t('reservations:details.approvedBy'),
    value: approvedByUser && `${approvedByUser.firstName} ${approvedByUser.lastName}`,
    hidden: !approvedByUser,
  },
  {
    title: t('reservations:details.cancelledOn'),
    value:
      cancelledAt &&
      format(toZonedTime(cancelledAt, timezone), 'MM/dd/yyyy hh:mm a', { timeZone: timezone }),
    hidden: !cancelledAt,
  },
  {
    title: t('reservations:details.cancelledByRole'),
    value: cancelledBy,
    hidden: !cancelledBy,
  },
  {
    title: t('reservations:details.cancelledBy'),
    value: cancelledByUser && `${cancelledByUser.firstName} ${cancelledByUser.lastName}`,
    hidden: !cancelledByUser,
  },
  {
    title: t('reservations:details.refunded'),
    value: refunded ? t('reservations:details.yes') : t('reservations:details.no'),
    hidden: !cancelled,
  },
  {
    title: t('reservations:details.refundDate'),
    value:
      refundedAt &&
      format(toZonedTime(refundedAt, timezone), 'MM/dd/yyyy hh:mm a', { timeZone: timezone }),
    hidden: !refundedAt,
  },
  {
    title: t('reservations:details.refundBy'),
    value: refundedByUser && `${refundedByUser.firstName} ${refundedByUser.lastName}`,
    hidden: !refundedByUser,
  },
  {
    title: t('reservations:details.refundAmount'),
    value: `$${refundAmount}`,
    hidden: !refundAmount,
  },
]

export const mapPaymentInformation = (
  { discount, chargeAmount, extraFee, createdAt },
  timezone,
  t,
) => [
  {
    title: t('reservations:details.discount'),
    value: `$${discount}`,
    hidden: !discount,
  },
  {
    title: t('reservations:details.totalAmount'),
    value: `$${chargeAmount}`,
    hidden: !chargeAmount,
  },
  {
    title: t('reservations:details.processingFee'),
    value: `$${extraFee}`,
    hidden: !extraFee,
  },
  {
    title: t('reservations:details.paidAmount'),
    value: `$${chargeAmount}`,
  },
  {
    title: t('reservations:details.paymentDate'),
    value:
      createdAt &&
      format(toZonedTime(createdAt, timezone), 'MM/dd/yyyy hh:mm a', { timeZone: timezone }),
  },
]

export const mapReservationsAnalytics = (analytics, t) => [
  {
    id: 0,
    title: t('reservations:analytics.totalReservations'),
    value: analytics.totalReservations,
    icon: <Icon icon="notebook" viewBox="3 0 20 20" />,
  },
  {
    id: 5,
    title: t('reservations:analytics.cancelledTotal'),
    value: analytics.cancelledTotal,
    icon: <Icon icon="downGraph" viewBox="2 0 20 20" />,
  },
  {
    id: 1,
    title: t('reservations:analytics.grossRevenue'),
    value: `$${analytics.grossRevenue || 0}`,
    icon: <Icon icon="upGraph" viewBox="0 0 20 15" />,
  },
  {
    id: 6,
    title: t('reservations:analytics.paidReservationsGross'),
    value: analytics.paidReservationsGrossTotal,
    icon: <Icon icon="upGraph" viewBox="0 0 20 15" />,
  },
  {
    id: 2,
    title: t('reservations:analytics.cancellationsRefunds'),
    value: `$${analytics.cancellationsRefunds || 0}`,
    icon: <Icon icon="downGraph" viewBox="2 0 20 20" />,
  },
  {
    id: 7,
    title: t('reservations:analytics.refundedTotal'),
    value: analytics.refundedTotal,
    icon: <Icon icon="downGraph" viewBox="2 0 20 20" />,
  },
  {
    id: 3,
    title: t('reservations:analytics.discountsPromotions'),
    value: `$${analytics.discountsPromotions || 0}`,
    icon: <Icon icon="discountLabel" viewBox="3 0 20 20" />,
  },
  {
    id: 8,
    title: t('reservations:analytics.discountedTotal'),
    value: analytics.discountedTotal,
    icon: <Icon icon="discountLabel" viewBox="3 0 20 20" />,
  },
  {
    id: 4,
    title: t('reservations:analytics.netRevenue'),
    value: `$${analytics.netRevenue || 0}`,
    icon: <Icon icon="moneyBag" viewBox="3 0 18 18" />,
  },
  {
    id: 9,
    title: t('reservations:analytics.paidReservationsNet'),
    value: analytics.paidReservationsNetTotal,
    icon: <Icon icon="moneyBag" viewBox="3 0 18 18" />,
  },
]

export const conflictReservationsColumns = ({ timezone, multiDayBooking }, t) => {
  return multiDayBooking
    ? [
        {
          Header: t('reservations:conflictTable.residentGuest'),
          id: 'residentGuest',

          Cell: ({ row: { original } }) => {
            const { residentName, guestName, phone, residentUnit } = original

            return (
              <TextCell
                title={residentName || guestName}
                text={residentUnit ? `#${residentUnit}` : phone}
              />
            )
          },
        },
        {
          Header: t('reservations:conflictTable.checkInDate'),
          accessor: 'date',
          Cell: ({ value }) =>
            `${format(toZonedTime(value, timezone), 'MM/dd/yyyy', { timeZone: timezone })}`,
        },
        {
          Header: t('reservations:conflictTable.checkOutDate'),
          accessor: 'endDate',
          Cell: ({ value }) =>
            `${format(toZonedTime(value, timezone), 'MM/dd/yyyy', { timeZone: timezone })}`,
        },
      ]
    : [
        {
          Header: t('reservations:conflictTable.residentGuest'),
          id: 'residentGuest',

          Cell: ({ row: { original } }) => {
            const { residentName, guestName, phone, residentUnit } = original

            return (
              <TextCell
                title={residentName || guestName}
                text={residentUnit ? `#${residentUnit}` : phone}
              />
            )
          },
        },
        {
          Header: t('reservations:conflictTable.date'),
          accessor: 'date',
          Cell: ({ value }) =>
            `${format(toZonedTime(value, timezone), 'MM/dd/yyyy', { timeZone: timezone })}`,
        },
        {
          Header: t('reservations:conflictTable.startTime'),
          accessor: 'startTime',
          Cell: ({ value }) => `${dayMinutesToHoursMinutes(value)}`,
        },
        {
          Header: t('reservations:conflictTable.endTime'),
          accessor: 'endTime',
          Cell: ({ value }) => `${dayMinutesToHoursMinutes(value)}`,
        },
      ]
}

const getAmenityPriceCellText = (row, t) => {
  const slots = row.multiDayBooking ? row.multidaySlots || [] : row.hourlySlots || []

  const prices = slots.map((slot) => Number(slot.price) || 0)

  if (prices.length === 0) {
    return <div className="free-badge">{t('reservations:amenitiesTable.free')}</div>
  }

  const uniquePrices = [...new Set(prices)]
  const minPrice = Math.min(...uniquePrices)
  const maxPrice = Math.max(...uniquePrices)

  if (maxPrice === 0) {
    return <div className="free-badge">{t('reservations:amenitiesTable.free')}</div>
  }

  if (uniquePrices.length === 1) {
    return `$${uniquePrices[0]}`
  }

  return `$${minPrice}–$${maxPrice}`
}

export const reservationsAmenitiesColumns = (t) => [
  {
    Header: t('reservations:amenitiesTable.image'),
    accessor: 'image',
    Cell: ({ value }) => (
      <div className="amenity-image">
        <img
          src={getAssetLink({
            path: 'chitown-building',
            file: value,
            fallback: { file: 'default_amenity.png' },
          })}
          alt={t('reservations:amenityForms.details.amenityImageAlt')}
        />
      </div>
    ),
  },
  {
    Header: t('reservations:amenitiesTable.name'),
    accessor: 'name',
  },
  {
    Header: t('reservations:amenitiesTable.description'),
    accessor: 'description',
  },
  {
    Header: t('reservations:amenitiesTable.type'),
    accessor: 'multiDayBooking',
    Cell: ({ value }) =>
      value ? t('reservations:amenitiesTable.multiday') : t('reservations:amenitiesTable.hourly'),
  },
  {
    Header: t('reservations:amenitiesTable.spaces'),
    accessor: 'locations',
    Cell: ({ value }) =>
      value?.length > 0
        ? t('reservations:amenitiesTable.multiple')
        : t('reservations:amenitiesTable.single'),
  },
  {
    Header: t('reservations:amenitiesTable.availableForBooking'),
    accessor: 'hidden',
    Cell: ({ value }) =>
      value ? t('reservations:amenitiesTable.no') : t('reservations:amenitiesTable.yes'),
  },
  {
    Header: t('reservations:amenitiesTable.price'),
    accessor: 'price',
    Cell: ({ row }) => {
      return getAmenityPriceCellText(row.original, t)
    },
  },
  {
    Header: t('reservations:amenitiesTable.actions'),
    accessor: 'actions',
    Cell: ({ row }) => (
      <Link to={`/dashboard/building-dashboard/reservations/amenities/edit/${row.original.id}`}>
        <button className="button button-transparent">
          <PencilSimple size={16} />
          {t('common:actions.edit')}
        </button>
      </Link>
    ),
  },
]

export const getStatusCountText = (status, data = {}) => {
  const key = `count${startCase(status)}`

  return data[key]
}

export const getNamedHandler = (handleFieldChange) => {
  return (value, fieldName) => handleFieldChange({ target: { name: fieldName, value } })
}

export const getTimeBlockEndTime = (startTime, endTime, timeInterval) =>
  (endTime || startTime) + timeInterval

export const buildTimeBlockSummaryText = (
  { amenity, date, endDate, startTime, endTime, blockFullDay, blockMultipleDays, timezone },
  t,
) => {
  if (amenity && amenity.multiDayBooking) {
    return t('reservations:timeBlockSummary.multiDayScheduled', {
      startDate: format(toZonedTime(date, timezone), 'MMMM dd', { timeZone: timezone }),
      startTime: blockFullDay ? '00:00am' : dayMinutesToHoursMinutes(amenity.checkInTime),
      endDate: format(toZonedTime(endDate, timezone), 'MMMM dd', { timeZone: timezone }),
      endTime: blockFullDay ? '23:59pm' : dayMinutesToHoursMinutes(amenity.checkOutTime),
      year: format(toZonedTime(endDate, timezone), 'yyyy', { timeZone: timezone }),
    })
  } else {
    if (!blockMultipleDays && typeof startTime === 'number') {
      return t('reservations:timeBlockSummary.singleDayScheduled', {
        date: format(toZonedTime(date, timezone), 'MMMM dd', { timeZone: timezone }),
        year: format(toZonedTime(date, timezone), 'yyyy', { timeZone: timezone }),
        startTime: dayMinutesToHoursMinutes(startTime),
        endTime: dayMinutesToHoursMinutes(
          getTimeBlockEndTime(startTime, endTime, amenity.timeInterval),
        ),
      })
    }

    return t('reservations:timeBlockSummary.singleDayAllDayScheduled', {
      date: format(toZonedTime(date, timezone), 'MMMM dd', { timeZone: timezone }),
      endDate: format(toZonedTime(endDate, timezone), 'MMMM dd', { timeZone: timezone }),
      year: format(toZonedTime(endDate, timezone), 'yyyy', { timeZone: timezone }),
    })
  }
}

export const getCustomTimeIntervals = (t) => [
  t('reservations:customTimeIntervals.next7Days'),
  t('reservations:customTimeIntervals.next30Days'),
]

export const allowTwoDecimalsAfterDot = (priceStr) => priceStr.replace(/^(\d+\.\d{2})\d+$/gm, '$1')

export const filterOutUnavailableSpaces = (locations = [], reservation) => {
  const { amenityId = {}, locationId } = reservation
  const { multiDayBooking, capacity } = amenityId
  if (multiDayBooking) {
    return locations.filter(
      (location) =>
        location?.available && location?.reservations === 0 && location?.id !== locationId?.id,
    )
  }

  return locations.filter(
    (location) =>
      location?.available && location?.reservations < capacity && location?.id !== locationId?.id,
  )
}
